import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../hooks/useProfile';
import { useTranslation } from 'react-i18next';
import { Title } from '../components/ui/Title';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { MediaUpload, type MediaData } from '../components/MediaUpload';

const createStorySchema = z.object({
  title: z.string().min(3).max(100),
  content: z.string().min(10),
  media: z.object({
    type: z.enum(['image', 'video', 'audio', 'external']),
    url: z.string().url(),
    method: z.enum(['upload', 'url']),
  }).optional(),
});

type CreateStoryFormData = z.infer<typeof createStorySchema>;

function CreateStory() {
  const { t } = useTranslation();
  const { bandSlug } = useParams<{ bandSlug: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { profile } = useProfile();
  const [error, setError] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [media, setMedia] = React.useState<MediaData | null>(null);

  const { register, handleSubmit, formState: { errors } } = useForm<CreateStoryFormData>({
    resolver: zodResolver(createStorySchema),
  });

  const onSubmit = async (data: CreateStoryFormData) => {
    if (isSubmitting) return; // Prevent multiple submissions
    
    if (!user || !profile) {
      setError('You must be logged in to create a story');
      return;
    }

    try {
      setIsSubmitting(true);
      // First get the band ID from the slug
      const { data: band, error: bandError } = await supabase
        .from('bands')
        .select('id')
        .eq('slug', bandSlug)
        .single();

      if (bandError) throw bandError;
      if (!band) throw new Error('Band not found');

      // Create the story
      const { error: storyError } = await supabase
        .from('stories')
        .insert([
          {
            title: data.title,
            content: data.content,
            image_url: media?.url || null,
            band_id: band.id,
            author_id: profile.id, // Use profile.id instead of user.id
          },
        ]);

      if (storyError) throw storyError;
      navigate(`/bands/${bandSlug}`);
    } catch (err) {
      console.error('Error creating story:', err);
      setError(err instanceof Error ? err.message : 'Failed to create story');
    } finally {
      setIsSubmitting(false);
    }
  };

  const breadcrumbItems = [
    { label: t('nav.bands'), href: '/bands' },
    { label: 'Band Details', href: `/bands/${bandSlug}` },
    { label: t('stories.createStory.title') },
  ];

  return (
    <div className="container mx-auto px-6 pb-8 md:py-24">
      <div className="mb-6">
        <Breadcrumbs items={breadcrumbItems} />
      </div>

      <Title subtitle={t('stories.createStory.subtitle')}>
        {t('stories.createStory.title')}
      </Title>

      <div className="max-w-4xl mx-auto">
        {error && (
          <div className="bg-red-500/10 text-red-500 p-4 rounded-lg mb-6 text-center">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
          <div className="panel">
            <div className="space-y-6">
              <div>
                <label htmlFor="title" className="block text-sm font-medium text-light/80">
                  {t('forms.title')}
                </label>
                <input
                  type="text"
                  id="title"
                  {...register('title')}
                  placeholder={t('forms.enterTitle')}
                  className="mt-1 block w-full rounded-lg bg-dark border border-gray-custom/20 px-4 py-2 text-light focus:border-gray-custom/40 focus:outline-none"
                />
                {errors.title && (
                  <p className="mt-1 text-sm text-red-500">{errors.title.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="content" className="block text-sm font-medium text-light/80">
                  {t('forms.content')}
                </label>
                <textarea
                  id="content"
                  rows={8}
                  {...register('content')}
                  placeholder={t('forms.enterContent')}
                  className="mt-1 block w-full rounded-lg bg-dark border border-gray-custom/20 px-4 py-2 text-light focus:border-gray-custom/40 focus:outline-none"
                />
                {errors.content && (
                  <p className="mt-1 text-sm text-red-500">{errors.content.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-light/80 mb-4">
                  Media ({t('common.optional')})
                </label>
                <MediaUpload
                  value={media}
                  onChange={setMedia}
                  storageContainer="story-media"
                />
              </div>
            </div>
          </div>

          <div className="flex flex-col md:flex-row justify-end md:space-x-4">
            <button
              type="button"
              onClick={() => navigate(`/bands/${bandSlug}`)}
              className="btn-outline order-2 mt-6 md:mt-0"
              disabled={isSubmitting}
            >
              {t('common.cancel')}
            </button>
            <button 
              type="submit" 
              className="btn-secondary md:order-2"
              disabled={isSubmitting}
            >
              {isSubmitting ? t('common.saving') : t('forms.createStory')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CreateStory;